var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useRef, useState, useEffect, useContext } from 'react';
import cn from 'classnames';
import { Icons } from '@beeline/design-tokens/js/iconfont/icons';
import { PopupsContext } from '../../utils';
import { Button } from '../Button';
import { Divider } from '../Divider';
import { Days } from './days';
import { Months } from './months';
import { Years } from './years';
import { PANELS, MONTHS_NAMES } from './constants';
import { NavigationIcon } from './components/navigation-icon';
import { CalendarMobile } from './components/CalendarMobile';
import { getViewDate, hasDateLimit, isValidDateValue, getDateFromIsoString, isRangeMode, formatToISO, } from './utils';
import "./styles.css";
export var Calendar = function (props) {
    var value = props.value, _a = props.minValue, minValue = _a === void 0 ? '' : _a, _b = props.maxValue, maxValue = _b === void 0 ? '' : _b, _c = props.disabledStartDate, disabledStartDate = _c === void 0 ? false : _c, _d = props.disabledEndDate, disabledEndDate = _d === void 0 ? false : _d, className = props.className, datepickerRef = props.datepickerRef, onSelectDate = props.onSelectDate, onOutsideClick = props.onOutsideClick, onClickSelectDates = props.onClickSelectDates, _e = props.dataTestId, dataTestId = _e === void 0 ? 'calendar' : _e, _f = props.outputDateFormat, outputDateFormat = _f === void 0 ? "yyyy-MM-dd'T'HH:mm:ss'Z'" : _f, multiselect = props.multiselect, saveButtonTitle = props.saveButtonTitle, mobileView = props.mobileView, readonly = props.readonly;
    if (mobileView)
        return React.createElement(CalendarMobile, __assign({}, props));
    var rootElement = useContext(PopupsContext).rootElement;
    var calendarRef = useRef(null);
    var _g = useState(PANELS.DAYS), panel = _g[0], setPanel = _g[1];
    var _h = useState(getViewDate(value, minValue, maxValue)), viewDate = _h[0], setViewDate = _h[1];
    var viewYear = viewDate.getFullYear();
    var viewMonth = viewDate.getMonth();
    var minDate = getDateFromIsoString(minValue);
    var maxDate = getDateFromIsoString(maxValue);
    var selectedDate = isRangeMode(value)
        ? value.map(getDateFromIsoString)
        : getDateFromIsoString(value);
    useEffect(function () {
        if (isValidDateValue(value, minDate, maxDate)) {
            var updatedViewDate = getViewDate(value, minValue, maxValue);
            setViewDate(updatedViewDate);
        }
    }, [value]);
    useEffect(function () {
        var handleOutsideClick = function (event) {
            var targetElement = event === null || event === void 0 ? void 0 : event.target;
            var calendarElement = calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current;
            var datepickerElement = datepickerRef === null || datepickerRef === void 0 ? void 0 : datepickerRef.current;
            var isOutsideClick = ![datepickerElement, calendarElement].some(function (element) { return element && element.contains(targetElement); });
            if (isOutsideClick) {
                onOutsideClick === null || onOutsideClick === void 0 ? void 0 : onOutsideClick();
            }
        };
        var listenerContainer = rootElement || window;
        listenerContainer.addEventListener('mousedown', handleOutsideClick);
        return function () {
            return listenerContainer.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);
    var setDaysPanel = function () {
        setPanel(PANELS.DAYS);
    };
    var setMonthsPanel = function () {
        setPanel(PANELS.MONTHS);
    };
    var setYearsPanel = function () {
        setPanel(PANELS.YEARS);
    };
    var handleSelectDay = function (day) {
        if (readonly)
            return;
        var newDate = new Date(Date.UTC(viewDate.getFullYear(), viewDate.getMonth(), day));
        var toFormatedISO = function (date) { return formatToISO(date, outputDateFormat); };
        var newDateISO = toFormatedISO(newDate);
        if (isRangeMode(selectedDate)) {
            if (multiselect) {
                var newMultipleDates = void 0;
                var validSelectedDate = selectedDate.filter(function (date) { return date !== null; });
                var indexDate = validSelectedDate.findIndex(function (date) { return date.getTime() === newDate.getTime(); });
                if (indexDate === -1) {
                    newMultipleDates = __spreadArray(__spreadArray([], validSelectedDate, true), [newDate], false);
                }
                else {
                    validSelectedDate.splice(indexDate, 1);
                    newMultipleDates = __spreadArray([], validSelectedDate, true);
                }
                onSelectDate === null || onSelectDate === void 0 ? void 0 : onSelectDate(newMultipleDates.map(toFormatedISO));
                return;
            }
            /**
             Если параметры "стартовая" и "конечная" даты заблокированы, клик не обрабатывается
             * */
            if (disabledStartDate && disabledEndDate) {
                return;
            }
            var newDateRange = void 0;
            var isFilledRange = Boolean(selectedDate[1]);
            var isStartedRange = Boolean(selectedDate[0]);
            var startDate = selectedDate[0], endDate = selectedDate[1];
            var localizedStartDate = startDate ? toFormatedISO(startDate) : '';
            var localizedEndDate = endDate ? toFormatedISO(endDate) : '';
            // Если выбор только начальной даты заблокирован
            if (disabledStartDate) {
                newDateRange =
                    startDate && newDate < startDate
                        ? [localizedStartDate, localizedStartDate]
                        : [localizedStartDate, newDateISO];
                return onSelectDate === null || onSelectDate === void 0 ? void 0 : onSelectDate(newDateRange);
            }
            else if (disabledEndDate) {
                // Если выбор только конечной даты заблокирован
                newDateRange =
                    endDate && newDate > endDate
                        ? [localizedEndDate, localizedEndDate]
                        : [newDateISO, localizedEndDate];
                return onSelectDate === null || onSelectDate === void 0 ? void 0 : onSelectDate(newDateRange);
            }
            if (!isStartedRange) {
                newDateRange = [newDateISO, ''];
            }
            else if (!isFilledRange) {
                /* В заполненном состоянии, обновляем:
                 * стартовую дату, если новое значение меньше выбранного диапазона или новая дата замыкает диапазон в дате окончания
                 * конечную дату, если новое значение больше выбранного диапазона или новая дата замыкает диапазон в дате начала
                 */
                newDateRange =
                    (startDate && newDate < startDate) ||
                        (endDate && newDate.getTime() === endDate.getTime())
                        ? [newDateISO, localizedEndDate]
                        : [localizedStartDate, newDateISO];
            }
            else {
                /* Устанавливаем выбранное значение даты как начало диапазона в случаях:
                 * диапазон пуст, отсутствует стартовая дата
                 * новое значение даты меньше стартовой даты
                 */
                newDateRange = [newDateISO, ''];
            }
            onSelectDate === null || onSelectDate === void 0 ? void 0 : onSelectDate(newDateRange);
        }
        else {
            onSelectDate === null || onSelectDate === void 0 ? void 0 : onSelectDate(newDateISO);
        }
    };
    var handleSelectMonth = function (month) {
        setViewDate(new Date(viewDate.getFullYear(), month));
        setDaysPanel();
    };
    var setPrevMonth = function () {
        handleSelectMonth(viewDate.getMonth() - 1);
    };
    var setNextMonth = function () {
        handleSelectMonth(viewDate.getMonth() + 1);
    };
    var handleSelectYear = function (year) {
        setViewDate(new Date(year, viewDate.getMonth()));
        setDaysPanel();
    };
    var isDisabledMonthSwitch = function (monthStep) {
        if (minDate || maxDate) {
            var minMonth = minDate && new Date(minDate.getFullYear(), minDate.getMonth());
            var maxMonth = maxDate && new Date(maxDate.getFullYear(), maxDate.getMonth());
            var checkedMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() - monthStep);
            return hasDateLimit(checkedMonth, minMonth, maxMonth);
        }
        return false;
    };
    var getPanel = function () {
        switch (panel) {
            case PANELS.DAYS:
                return (React.createElement(Days, { minDate: minDate, maxDate: maxDate, viewDate: viewDate, disabledStartDate: disabledStartDate, disabledEndDate: disabledEndDate, selectedDate: selectedDate, onSelectDay: handleSelectDay, multiselect: multiselect, readonly: readonly }));
            case PANELS.MONTHS:
                return (React.createElement(Months, { minDate: minDate, maxDate: maxDate, viewDate: viewDate, showStartPanel: setDaysPanel, onSelectMonth: handleSelectMonth }));
            case PANELS.YEARS:
                return (React.createElement(Years, { selectedYear: viewDate.getFullYear(), minYear: minDate === null || minDate === void 0 ? void 0 : minDate.getFullYear(), maxYear: maxDate === null || maxDate === void 0 ? void 0 : maxDate.getFullYear(), onSelectYear: handleSelectYear, showStartPanel: setMonthsPanel }));
            default:
                return null;
        }
    };
    var footer = (React.createElement(React.Fragment, null,
        React.createElement(Divider, null),
        React.createElement("div", { className: 'lk-ui_calendar-footer' },
            React.createElement(Button, { variant: 'contained', onClick: onClickSelectDates }, saveButtonTitle || 'Сохранить'))));
    var hasFooter = multiselect && !readonly;
    return (React.createElement("div", { ref: calendarRef, className: cn('lk-ui_calendar', className), "data-test-id": dataTestId },
        React.createElement("div", { className: cn('lk-ui_calendar-panel', { multiselect: hasFooter }), "data-test-id": dataTestId },
            React.createElement("header", { className: 'lk-ui_calendar-header' },
                React.createElement("p", { className: 'lk-ui_calendar-title' },
                    React.createElement("span", { className: 'lk-ui_calendar-select-month', "data-test-id": dataTestId + "-select-month" }, MONTHS_NAMES[viewMonth]),
                    React.createElement("span", { className: 'lk-ui_calendar-select-year', "data-test-id": dataTestId + "-select-year" }, viewYear),
                    React.createElement(NavigationIcon, { direction: panel === PANELS.DAYS ? Icons.ArrowDropDown : Icons.ArrowDropUp, onClick: setYearsPanel, dataTestId: dataTestId + "-back" })),
                React.createElement("span", { className: 'lk-ui_calendar-navigation' },
                    React.createElement(NavigationIcon, { direction: Icons.NavArrowLeft, onClick: setPrevMonth, disabled: isDisabledMonthSwitch(1), dataTestId: dataTestId + "-prev-month" }),
                    React.createElement(NavigationIcon, { direction: Icons.NavArrowRight, onClick: setNextMonth, disabled: isDisabledMonthSwitch(-1), dataTestId: dataTestId + "-next-month" }))),
            getPanel()),
        hasFooter && footer));
};
